import React, { forwardRef, useImperativeHandle, useRef, useEffect, useMemo } from 'react'
import GridSelect from './GridSelect'
import { LOCATION_TYPES, FeeValidationErrorTypes } from '../constants/Validation'
import { FEE_MGMT_COLUMNS } from '../constants/Columns'
import { useEntityMapping } from '../hooks/useEntityMapping'
import { parseBranchCellNames, joinBranchNames } from '../utils/branchHelpers'

const isBranchField = (field) => field === FEE_MGMT_COLUMNS.BRANCH.columnKey

const NONE_OPTION = {
  value: '',
  label: '(None)',
  className: 'invalid-option',
}

const shouldClearLocation = (field, value) => {
  if (field !== FEE_MGMT_COLUMNS.COUNTIES.columnKey && field !== FEE_MGMT_COLUMNS.STATES.columnKey) {
    return false
  }
  if (!value) {
    return false
  }
  return value.includes(',')
}

const EntitySelectCellEditor = forwardRef((props, ref) => {
  const { value, onValueChange } = props
  const field = props.column.colId
  const rowData = props.node?.data

  // Determine if we should use multiple selection mode
  const isMultiSelect = useMemo(() => {
    const locationType = rowData.locationType

    // For states field, allow multiple selection only if location type is MULTI-STATE
    if (field === FEE_MGMT_COLUMNS.STATES.columnKey) {
      return locationType === LOCATION_TYPES.STATE
    }

    // For counties field, allow multiple selection only if location type is COUNTY
    if (field === FEE_MGMT_COLUMNS.COUNTIES.columnKey) {
      return locationType === LOCATION_TYPES.COUNTY
    }

    // A row may apply to several branches (multi-branch rows).
    if (field === FEE_MGMT_COLUMNS.BRANCH.columnKey) {
      return true
    }

    // Default to single selection
    return false
  }, [field, rowData.locationType])

  // Parse initial value depending on selection mode
  const parseInitialValue = () => {
    // If not multi-select, return value as is
    if (!isMultiSelect) {
      if (shouldClearLocation(field, value)) {
        return ''
      }

      return value
    }

    // For multi-select, handle both string and array types
    if (!value) {
      return []
    }

    if (Array.isArray(value)) {
      // If it's already an array, trim each element
      return value.map((val) => String(val).trim()).filter(Boolean)
    }

    if (typeof value === 'string') {
      // Branch cells use escaped-comma delimiting so comma-named branches
      // survive (e.g. "Boston, MA"); parse with the shared helper rather than a
      // blind comma split, which would shatter such a name into bogus tokens.
      if (isBranchField(field)) {
        return parseBranchCellNames(value, (options || []).map((opt) => ({ name: opt.value })))
      }

      // If it's a string, split by comma and trim each element
      return value
        .split(',')
        .map((val) => val.trim())
        .filter(Boolean)
    }

    // Return empty array for other unexpected types
    return []
  }

  const selectRef = useRef(null)
  const containerRef = useRef(null)
  const valueAppliedRef = useRef(false)

  // Pass the row data to useEntityMapping for county-specific filtering
  const { options, loading, refreshOptions } = useEntityMapping(field, props.api, rowData)

  // Modify enhancedOptions to include invalid values at the top
  const enhancedOptions = useMemo(() => {
    const validOptions = [...options]
    const invalidOptions = []

    // Get validation errors for this field
    const fieldErrors = rowData?.validationErrors?.[field]
    const invalidValues = new Set()

    // Extract invalid values from validation errors
    if (fieldErrors?.details) {
      Object.entries(fieldErrors.details).forEach(([errorType, error]) => {
        if (error.values) {
          error.values.forEach((detail) => {
            if (detail.value) {
              // Skip INVALID_FIELD_FOR_LOCATION_TYPE messages
              if (errorType === FeeValidationErrorTypes.INVALID_FIELD_FOR_LOCATION_TYPE) {
                return
              }
              // Split comma-separated values and trim
              let valuesToAdd = detail.value
              if (typeof valuesToAdd === 'string') {
                valuesToAdd = valuesToAdd.split(',')
              }
              valuesToAdd.forEach((val) => {
                const trimmedVal = val.trim()
                if (trimmedVal) {
                  invalidValues.add(trimmedVal)
                }
              })
            }
          })
        }
      })
    }

    // Create invalid options array
    invalidValues.forEach((invalidValue) => {
      // Only add if not already in options
      if (!validOptions.some((opt) => opt.value === invalidValue)) {
        invalidOptions.push({
          value: invalidValue,
          label: invalidValue,
          className: 'invalid-option',
        })
      }
    })

    let allOptions = [...invalidOptions, ...validOptions]

    // Add (None) option if needed
    if ([FEE_MGMT_COLUMNS.COUNTIES.columnKey].includes(field)) {
      allOptions = [NONE_OPTION, ...allOptions]
    }

    return allOptions
  }, [options, field, rowData?.validationErrors])

  // Modern AG Grid cell editor interface implementation
  useImperativeHandle(ref, () => {
    return {
      // AG Grid calls this when user hits Tab
      isCancelBeforeStart() {
        return false
      },
      // AG Grid calls this when escape is pressed
      isCancelAfterEnd() {
        return false
      },
      // Focus the editor when it's shown
      focusIn() {
        setTimeout(() => {
          selectRef.current?.focus()
        }, 0)
      },
      // Called after the editor is attached to the DOM
      afterGuiAttached() {
        selectRef.current?.focus()
      },
    }
  })

  // Update options when state changes for county column
  useEffect(() => {
    if (field === FEE_MGMT_COLUMNS.COUNTIES.columnKey) {
      refreshOptions()
    }
  }, [field, refreshOptions])

  // Initialize value from props
  useEffect(() => {
    // Focus the select when the editor is activated
    setTimeout(() => {
      selectRef.current?.focus()
    }, 100)

    valueAppliedRef.current = false
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleChange = (newValue) => {
    // Handle (None) selection for multi-select
    if (isMultiSelect && Array.isArray(newValue)) {
      // If (None) was just selected, clear all other selections
      if (newValue.includes(null)) {
        newValue = [null]
      } else if (value?.includes(null)) {
        newValue = newValue.filter((v) => v !== null)
      }
    }

    onValueChange(newValue)
    valueAppliedRef.current = true

    // Prepare value for updating the grid
    let valueToSet
    if (isMultiSelect && Array.isArray(newValue)) {
      if (isBranchField(field)) {
        // Escape commas in branch names so a comma-named branch round-trips.
        valueToSet = joinBranchNames(newValue.filter(Boolean))
      } else {
        valueToSet = newValue.filter(Boolean).join(', ') // Join array values with commas, filtering out null
      }
    } else {
      valueToSet = newValue // Single select value
    }

    // Save column state before making changes
    const currentColumnState = props.api?.getColumnState()

    // If this is a county selection and no state exists, update both county and state
    const rowStateValue = rowData?.[FEE_MGMT_COLUMNS.STATES.columnKey]

    const oldValueForUndo = { ...props.node.data }

    // Create a complete new data object for the transaction
    const newDataForUpdate = {
      ...oldValueForUndo,
      [props.column.colId]: valueToSet,
    }

    // Handle dependent field updates
    if (field === FEE_MGMT_COLUMNS.COUNTIES.columnKey && !rowStateValue) {
      // For counties, find the first selected option to get state info
      const selectedOption = Array.isArray(newValue)
        ? enhancedOptions.find((opt) => opt.value === newValue[0])
        : enhancedOptions.find((opt) => opt.value === newValue)

      if (selectedOption?.state) {
        newDataForUpdate[FEE_MGMT_COLUMNS.STATES.columnKey] = selectedOption.state
      }
    } else if (field === FEE_MGMT_COLUMNS.STATES.columnKey) {
      // If changing state, clear the county
      newDataForUpdate[FEE_MGMT_COLUMNS.COUNTIES.columnKey] = null
    }

    // Create a transaction-based redo callback
    const redoCallback = () => {
      props.api.applyTransactionAsync(
        {
          update: [newDataForUpdate],
        },
        () => {
          props.context.refreshValidation(true)
          // Refresh cells after transaction completes
          props.api.refreshCells({
            force: true,
            rowNodes: [props.node],
          })
        },
      )
    }

    // Record the edit for undo/redo
    props.context.recordEdit([props.node.data.rowNumber], [oldValueForUndo], redoCallback)

    // For the initial update, directly apply the new values to ensure immediate UI update
    props.node.setDataValue(props.column.colId, valueToSet)

    // Apply dependent field updates
    if (field === FEE_MGMT_COLUMNS.COUNTIES.columnKey && !rowStateValue) {
      const selectedOption = Array.isArray(newValue)
        ? enhancedOptions.find((opt) => opt.value === newValue[0])
        : enhancedOptions.find((opt) => opt.value === newValue)

      if (selectedOption?.state) {
        props.node.setDataValue(FEE_MGMT_COLUMNS.STATES.columnKey, selectedOption.state)
      }
    } else if (field === FEE_MGMT_COLUMNS.STATES.columnKey) {
      props.node.setDataValue(FEE_MGMT_COLUMNS.COUNTIES.columnKey, null)
    }

    props.context.refreshValidation(true) // Pass true to suppress sorting

    // Restore column state after changes
    if (currentColumnState) {
      setTimeout(() => {
        props.api?.applyColumnState({
          state: currentColumnState,
          applyOrder: true,
        })
      }, 0)
    }

    // Important: Ensure we don't lose focus
    setTimeout(() => {
      if (selectRef.current) {
        selectRef.current.focus()
      }
    }, 0)
  }

  if (loading) {
    return null
  }

  return (
    <div className="entity-select-editor" ref={containerRef}>
      <GridSelect
        showSearch
        ref={selectRef}
        value={parseInitialValue()}
        onChange={handleChange}
        options={enhancedOptions}
        mode={isMultiSelect ? 'multiple' : undefined}
        optionFilterProp="label"
        filterOption={(input, option) => (option?.label ?? '').toLowerCase().includes(input.toLowerCase())}
        listHeight={200}
        autoFocus={true}
        defaultOpen={true}
        showArrow={true}
        maxTagCount={isMultiSelect ? 'responsive' : undefined}
      />
    </div>
  )
})

EntitySelectCellEditor.displayName = 'EntitySelectCellEditor'

export default EntitySelectCellEditor
